import { useMemo } from 'react';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import type { NullableDateInput } from '@localization/formatters/dateFormatter';
// These EventDetails imports are guest-safe — no admin dependencies: EVENT_DETAILS_SCHEMA_URL is a
// string constant, EventDetailsData is type-only, and resolveEventDateDisplay depends only on
// @localization + the type file.
/* eslint-disable no-restricted-imports */
import { EVENT_DETAILS_SCHEMA_URL } from '@website-custom-components/EventDetails/EventDetails.constants';
import type { EventDetailsData } from '@website-custom-components/EventDetails/EventDetails.types';
import { resolveEventDateDisplay } from '@website-custom-components/EventDetails/resolveEventDateDisplay';
/* eslint-enable no-restricted-imports */
import { PageKind } from '@graphql/generated-base';
import { useCustomData } from '../hooks/useCustomData';

type UseWelcomeEventDateDisplayArgs = Readonly<{
  eventHandle: string;
  eventId: string;
  startTimestamp: NullableDateInput;
  timezone?: string | null;
}>;

// Reads the welcome page's EventDetails block and returns only its multi-day date override — a saved
// range or custom text (PRNT-4321) — so other surfaces can render the event date the way the welcome
// page does. Returns undefined when the event has no saved range, so callers keep their existing
// single date and this stays purely additive.
export const useWelcomeEventDateDisplay = ({ eventHandle, eventId, startTimestamp, timezone }: UseWelcomeEventDateDisplayArgs) => {
  const { pageContainerBySlug, loading } = useCustomData(eventId, eventHandle, 'welcome');
  const { formatDate, formatEventDateRange } = useEventLocaleFormatter();

  const content = useMemo<EventDetailsData | undefined>(() => {
    // Only trust the system/migrated welcome page — a user-created custom page squatting the 'welcome'
    // slug (pageKind userCustom) must not override the registry date. Mirrors the systemCustom guard
    // used for the registry page container in GuestRegistry.controller.
    if (pageContainerBySlug?.pageKind !== PageKind.systemCustom) {
      return undefined;
    }
    const block = pageContainerBySlug.components?.find(
      component => component.__typename === 'JsonDataBlobComponent' && component.containerContent?.schemaUrl === EVENT_DETAILS_SCHEMA_URL,
    );
    return block?.__typename === 'JsonDataBlobComponent' ? (block.containerContent.content as EventDetailsData) : undefined;
  }, [pageContainerBySlug]);

  // Only a saved range / custom text produces an override; single-date events return undefined.
  const options = { titlecase: true, timezone };
  const eventDateDisplay = content?.dateDisplayType === 'dateRange' ? resolveEventDateDisplay(content, startTimestamp, options, { formatDate, formatEventDateRange }) : undefined;

  return { eventDateDisplay, loading };
};
